import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminAddTenantWizard from '@/components/admin/AdminAddTenantWizard';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Search, Plus, ExternalLink, ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Tenant {
  id: string;
  name: string;
  industry: string | null;
  status: string | null;
  contact_email: string | null;
  created_at: string | null;
  source: 'legacy' | 'advisory';
}

const statusColors: Record<string, string> = {
  onboarding: 'bg-blue-500/10 text-blue-500',
  intake: 'bg-sky-500/10 text-sky-500',
  building: 'bg-yellow-500/10 text-yellow-500',
  preview: 'bg-purple-500/10 text-purple-500',
  live: 'bg-green-500/10 text-green-500',
  paused: 'bg-gray-500/10 text-gray-500',
};

const getNextAction = (status: string | null): { label: string; path: string } | null => {
  switch (status) {
    case 'onboarding':
      return { label: 'Complete intake', path: 'overview' };
    case 'intake':
      return { label: 'Review assets', path: 'assets' };
    case 'building':
      return { label: 'Configure bot', path: 'bots' };
    case 'preview':
      return { label: 'Approve & deploy', path: 'bots' };
    case 'live':
      return { label: 'View reports', path: 'reports' };
    default:
      return null;
  }
};

const AdminTenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      
      // Query both tenant tables in parallel
      const [legacyRes, advisoryRes] = await Promise.all([
        supabase.from('tenants').select('id, name, industry, status, client_contact_email, created_at').order('created_at', { ascending: false }),
        supabase.from('advisory_tenants').select('id, name, industry, status, owner_email, created_at').order('created_at', { ascending: false }),
      ]);

      // Normalize legacy tenants to common interface
      const legacyTenants: Tenant[] = (legacyRes.data || []).map(t => ({
        id: t.id,
        name: t.name,
        industry: t.industry,
        status: t.status,
        contact_email: t.client_contact_email,
        created_at: t.created_at,
        source: 'legacy' as const,
      }));

      // Normalize advisory tenants
      const advisoryTenants: Tenant[] = (advisoryRes.data || []).map(t => ({
        id: t.id,
        name: t.name,
        industry: t.industry,
        status: t.status,
        contact_email: t.owner_email,
        created_at: t.created_at,
        source: 'advisory' as const,
      }));

      // Merge and sort by created_at descending
      const allTenants = [...legacyTenants, ...advisoryTenants]
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());

      setTenants(allTenants);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.contact_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusCounts = tenants.reduce((acc, tenant) => {
    const status = tenant.status || 'onboarding';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Tenants</h1>
            <p className="text-muted-foreground mt-1">
              Manage client organizations and their assistants
            </p>
          </div>
          <Button onClick={() => setWizardOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Tenant
          </Button>
        </div>

        <AdminAddTenantWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          onSuccess={fetchTenants}
        />

        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {['onboarding', 'intake', 'building', 'preview', 'live', 'paused'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              className={`p-3 rounded-lg border transition-colors text-left ${
                statusFilter === status 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <p className="text-2xl font-bold">{statusCounts[status] || 0}</p>
              <p className="text-xs text-muted-foreground capitalize">{status}</p>
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="intake">Intake</SelectItem>
                  <SelectItem value="building">Building</SelectItem>
                  <SelectItem value="preview">Preview</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredTenants.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No tenants found</h3>
                <p className="text-muted-foreground mt-1">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Get started by adding your first tenant'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Next Action</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTenants.map((tenant) => {
                      const nextAction = getNextAction(tenant.status);
                      return (
                        <TableRow key={tenant.id}>
                          <TableCell className="font-medium">{tenant.name}</TableCell>
                          <TableCell className="capitalize">{tenant.industry || '—'}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="secondary"
                              className={statusColors[tenant.status || 'onboarding']}
                            >
                              {tenant.status || 'onboarding'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {nextAction ? (
                              <Link 
                                to={`/admin/tenants/${tenant.id}?tab=${nextAction.path}`}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                {nextAction.label}
                                <ArrowRight className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {tenant.contact_email || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Link to={`/admin/tenants/${tenant.id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminTenants;
